// =============================================================================
// Component: ReportStatusBadge
// Shows the current status of report generation for a submission
// =============================================================================

import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import type { JobStatus } from "@/hooks/useReportQueue";

interface ReportStatusBadgeProps {
  status: "none" | "pending" | "processing" | "completed" | "failed";
  className?: string;
  showIcon?: boolean;
}

const statusConfig: Record<
  ReportStatusBadgeProps["status"],
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: React.ReactNode;
  }
> = {
  none: {
    label: "Sin generar",
    variant: "outline",
    icon: <Clock className="h-3 w-3" />,
  },
  pending: {
    label: "En cola",
    variant: "secondary",
    icon: <Clock className="h-3 w-3" />,
  },
  processing: {
    label: "Generando...",
    variant: "default",
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  completed: {
    label: "Generado",
    variant: "default",
    icon: <CheckCircle className="h-3 w-3" />,
  },
  failed: {
    label: "Error",
    variant: "destructive",
    icon: <XCircle className="h-3 w-3" />,
  },
};

export function ReportStatusBadge({
  status,
  className,
  showIcon = true,
}: ReportStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} className={className}>
      {showIcon && <span className="mr-1">{config.icon}</span>}
      {config.label}
    </Badge>
  );
}

// =============================================================================
// Helper to convert job status to badge status
// =============================================================================

export function getReportStatus(
  hasReport: boolean,
  activeJobStatus?: JobStatus | null,
  hasFailed?: boolean
): ReportStatusBadgeProps["status"] {
  if (activeJobStatus === "processing") return "processing";
  if (activeJobStatus === "pending") return "pending";
  if (hasFailed && !hasReport) return "failed";
  if (hasReport) return "completed";
  return "none";
}
